import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/AutoCompleteVirtualize';
import { Button } from '@openx/components-core/lib/Button/Button';
import type { DealApi } from '@openx/types/deal';
import { produceSortedOptionsKeys } from '@openx/utils/lib/sortedOptions';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealContext } from '../../../utils/context/DealContext';
import { DSP_OPTION_ALL, getTooltipText, type TimePeriod } from '../constants';
import { type DateRange, TroubleshootingDateFilter } from './TroubleshootingDateFilter';

const styles: { [key: string]: SxProps<Theme> } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme => theme.spacing(3.5, 0, 0),
    gap: theme => theme.spacing(2),
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'flex-start',
    marginLeft: 4,
    marginRight: 4,
    gap: theme => theme.spacing(2),
    overflow: 'visible',
  },
  filtersRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    columnGap: theme => theme.spacing(6),
    rowGap: theme => theme.spacing(1.5),
    flex: 1,
    minWidth: 0,
    overflow: 'visible',
  },
  controlsRight: {
    display: 'flex',
    alignItems: 'flex-end',
    flexShrink: 0,
    marginLeft: 'auto',
  },
  label: {
    display: 'flex',
    lineHeight: '12px',
    marginLeft: theme => theme.spacing(1.25),
    paddingBottom: theme => theme.spacing(0.5),
  },
  dspContainer: {
    minWidth: '400px',
    width: '400px',
    flexShrink: 0,
  },
  dateRangeContainer: {
    minWidth: '400px',
    overflow: 'visible',
  },
};

interface TroubleshootingControlsProps {
  dealId: string;
  dealParticipants: DealApi['deal_participants'];
  selectedPeriod: TimePeriod;
  customDateRange?: DateRange;
  selectedDspId: string;
  onPeriodChange: (period: TimePeriod, dateRange?: DateRange) => void;
  onDspChange: (dspId: string) => void;
  onExportCharts: () => void;
  onExportFunnel: () => void;
  lastUpdated?: string | null;
  isLoading?: boolean;
}

export const TroubleshootingControls = ({
  dealParticipants,
  selectedPeriod,
  customDateRange,
  selectedDspId,
  onPeriodChange,
  onDspChange,
  onExportCharts,
  onExportFunnel,
  lastUpdated,
  isLoading,
}: TroubleshootingControlsProps) => {
  const { t } = useTranslation();
  const { useDemandPartnersFetch, useOnBehalfOfAccountId } = useDealContext();
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);

  const onBehalfOfAccountId = useOnBehalfOfAccountId();
  const { mapById: demandPartners = {}, loading: demandPartnersLoading } = useDemandPartnersFetch({
    account_uid: onBehalfOfAccountId,
  });

  const isMultiDspDeal = dealParticipants.length > 1;

  const sortedDemandPartnerOptions = useMemo(() => {
    const demandPartnerOptions = produceSortedOptionsKeys(demandPartners).filter(demandPartnerId => {
      return dealParticipants.some(item => item.demand_partner === demandPartnerId);
    });
    return [DSP_OPTION_ALL].concat(demandPartnerOptions);
  }, [demandPartners, dealParticipants]);

  const getOptionLabel = useCallback(
    (option: string) => {
      if (option === DSP_OPTION_ALL) {
        return t('All');
      }
      return demandPartners[option]?.name ?? t('N / A');
    },
    [demandPartners, t]
  );

  const handleExportMenuClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  }, []);

  const handleExportMenuClose = useCallback(() => {
    setExportMenuAnchor(null);
  }, []);

  const handleExportChartsClick = useCallback(() => {
    onExportCharts();
    handleExportMenuClose();
  }, [onExportCharts, handleExportMenuClose]);

  const handleExportFunnelClick = useCallback(() => {
    onExportFunnel();
    handleExportMenuClose();
  }, [onExportFunnel, handleExportMenuClose]);

  const lastUpdatedInfo = useMemo(() => {
    return getTooltipText(selectedPeriod, lastUpdated, t);
  }, [selectedPeriod, lastUpdated, t]);

  return (
    <Box sx={styles.container} data-test="troubleshooting-controls">
      <Box sx={styles.controlsRow}>
        <Box sx={styles.filtersRow}>
          <Box sx={styles.dateRangeContainer}>
            <Typography
              variant="caption"
              sx={[
                {
                  color: 'text.secondary',
                },
                ...(Array.isArray(styles.label) ? styles.label : [styles.label]),
              ]}
            >
              {t('Date Range')}
            </Typography>
            <TroubleshootingDateFilter
              selectedPeriod={selectedPeriod}
              customDateRange={customDateRange}
              onPeriodChange={onPeriodChange}
              lastUpdated={lastUpdated}
            />
          </Box>
          {isMultiDspDeal && (
            <Box sx={styles.dspContainer}>
              <Typography
                variant="caption"
                sx={[
                  {
                    color: 'text.secondary',
                  },
                  ...(Array.isArray(styles.label) ? styles.label : [styles.label]),
                ]}
              >
                {t('DSP')}
              </Typography>
              <AutoCompleteVirtualize
                value={selectedDspId}
                onChange={(_, newValue) => onDspChange(newValue || DSP_OPTION_ALL)}
                options={sortedDemandPartnerOptions}
                getOptionLabel={getOptionLabel}
                disableClearable
                loading={demandPartnersLoading}
                disabled={isLoading}
                renderInput={params => <TextField {...params} data-test="dsp-dropdown" />}
                data-test="dsp-autocomplete"
              />
            </Box>
          )}
        </Box>
        <Box sx={styles.controlsRight}>
          <Button
            onClick={handleExportMenuClick}
            variant="contained"
            color="primary"
            endIcon={<KeyboardArrowDown />}
            data-test="export-csv-button"
            disabled={isLoading}
          >
            {t('EXPORT CSV')}
          </Button>
          <Menu
            anchorEl={exportMenuAnchor}
            open={exportMenuOpen}
            onClose={handleExportMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            data-test="export-csv-menu"
          >
            <MenuItem onClick={handleExportChartsClick} data-test="export-charts-menu-item">
              {t('Charts')}
            </MenuItem>
            <MenuItem onClick={handleExportFunnelClick} data-test="export-funnel-menu-item">
              {t('Funnel')}
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      {lastUpdatedInfo && (
        <Box
          sx={{
            mt: 2,
          }}
        >
          <Alert data-test="last-updated-info" severity="info">
            {lastUpdatedInfo}
          </Alert>
        </Box>
      )}
    </Box>
  );
};
